/**
 * Память журнала аудита в браузере (задача 38, этап B).
 *
 * Зачем: секцию открывают, переключают период туда-обратно и нажимают «Обновить»; каждый
 * такой показ не должен стоить запроса к функции. Показанный журнал запоминается на
 * период на короткий срок (`AUDIT_CACHE_TTL_MS`), а вместе с ним — версия ответа
 * (`ETag`): даже когда память устарела, запрос становится условным и при неизменных
 * записях получает `304` без тела (образец — `organizerCache`).
 *
 * Срок короткий намеренно: журнал показывает чужие действия, и «вчерашняя» таблица
 * выглядела бы как «ничего не происходило». Кнопка «Обновить» память игнорирует — свежий
 * срез важнее экономии запроса.
 *
 * Память живёт в пределах вкладки и **привязана к токену сессии**: после выхода и входа
 * другого пользователя сохранённый журнал не показывается (иначе чужому администратору
 * мелькнули бы записи прежней сессии).
 */
import type { AuditPeriod, AuditScreen } from '../shared/audit';
import { getSessionToken } from './session';

/** Сколько живёт сохранённый журнал: дольше — таблица могла бы показывать старый срез. */
export const AUDIT_CACHE_TTL_MS = 60_000;

/** Сохранённый журнал периода, его версия и момент показа. */
interface CachedAudit {
  /** Токен сессии, которой журнал принадлежит. */
  token: string | null;
  /** Момент показа (мс): по нему считается срок жизни памяти. */
  at: number;
  screen: AuditScreen;
  etag?: string;
}

const cache = new Map<AuditPeriod, CachedAudit>();

/** Запись памяти текущей сессии, не истёкшая по сроку; иначе undefined. */
function fresh(period: AuditPeriod): CachedAudit | undefined {
  const entry = cache.get(period);
  if (!entry || entry.token !== getSessionToken()) return undefined;
  return Date.now() - entry.at < AUDIT_CACHE_TTL_MS ? entry : undefined;
}

/** Свежий журнал периода или undefined: SPA показывает его без запроса к API. */
export function cachedAuditScreen(period: AuditPeriod): AuditScreen | undefined {
  return fresh(period)?.screen;
}

/** Версия сохранённого журнала периода: её отправляет условный запрос (`If-None-Match`). */
export function auditScreenEtag(period: AuditPeriod): string | undefined {
  return fresh(period)?.etag;
}

/** Запоминает показанный журнал вместе с версией ответа (или продлевает срок памяти). */
export function rememberAuditScreen(period: AuditPeriod, screen: AuditScreen, etag?: string): void {
  cache.set(period, { token: getSessionToken(), at: Date.now(), screen, etag });
}